<script setup>
import { ref, computed, onMounted } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import CaptionService from '@/services/CaptionService'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import Alert from '@/components/Atoms/Alert.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconEye from '@/components/icon/IconEye.vue'
import IconEyeCrossed from '@/components/icon/IconEyeCrossed.vue'
import { normalizeProvider } from './captionSyncWords'
import { track, EVENTS } from '@/lib/tracking'

/**
 * Settings -> Developers: the account's own SyncWords keys. Connected, new
 * captioning runs on that SyncWords account (its minutes, not Castr's plan).
 * The keys are sent once on Connect and never come back from core; they live
 * only in this form's inputs and are cleared once connected.
 */
const { notify } = useNotification()

const loading = ref(true)
const loadError = ref('')
const provider = ref(normalizeProvider(null))

const apiKey = ref('')
const apiSecret = ref('')
const revealSecret = ref(false)
const connecting = ref(false)
// { code, message } from the last failed connect.
const connectError = ref(null)

const disconnectOpen = ref(false)
const disconnecting = ref(false)
const disconnectError = ref('')
// [{ id, streamName }] from a 409 captions_provider_in_use.
const runningFlows = ref([])

const KEY_MIN = 10
const KEY_MAX = 256
const canConnect = computed(() => apiKey.value.trim().length >= KEY_MIN && apiSecret.value.trim().length >= KEY_MIN && !connecting.value)

const connectedLine = computed(() => {
	const p = provider.value
	const parts = []
	if (Number.isFinite(p.connectedAt)) parts.push(`Connected ${new Date(p.connectedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}`)
	else parts.push('Connected')
	if (p.connectedBy?.name || p.connectedBy?.email) parts.push(`by ${p.connectedBy.name || p.connectedBy.email}`)
	return parts.join(' ')
})

async function load() {
	loading.value = true
	loadError.value = ''
	try {
		provider.value = await CaptionService.getProvider()
	} catch (err) {
		loadError.value = err?.message || 'Could not load the captioning account.'
	} finally {
		loading.value = false
	}
}

async function connect() {
	if (!canConnect.value) return
	connecting.value = true
	connectError.value = null
	try {
		provider.value = await CaptionService.connectProvider({ apiKey: apiKey.value.trim(), apiSecret: apiSecret.value.trim() })
		apiKey.value = ''
		apiSecret.value = ''
		revealSecret.value = false
		track(EVENTS.CAPTIONING_PROVIDER_CONNECTED, {})
		const name = provider.value.account?.name
		notify({ group: 'success', text: name ? `SyncWords account ${name} connected. New captioning runs on it.` : 'SyncWords account connected. New captioning runs on it.' })
	} catch (err) {
		connectError.value = { code: err?.code || null, message: err?.message || 'Could not connect the account. Try again.' }
	} finally {
		connecting.value = false
	}
}

function openDisconnect() {
	disconnectError.value = ''
	runningFlows.value = []
	disconnectOpen.value = true
}

async function disconnect() {
	if (disconnecting.value) return
	disconnecting.value = true
	disconnectError.value = ''
	runningFlows.value = []
	try {
		provider.value = await CaptionService.disconnectProvider()
		disconnectOpen.value = false
		track(EVENTS.CAPTIONING_PROVIDER_DISCONNECTED, {})
		notify({ group: 'info', text: 'SyncWords account disconnected. New captioning runs on Castr\'s captioning service.' })
	} catch (err) {
		if (err?.code === 'captions_provider_in_use') runningFlows.value = err.runningFlows || []
		disconnectError.value = err?.message || 'Could not disconnect the account. Try again.'
	} finally {
		disconnecting.value = false
	}
}

onMounted(load)
</script>

<template>
	<section class="flex w-full flex-col gap-4 rounded-xl bg-surface-3/40 p-4 ring-1 ring-white/5 sm:p-5" aria-labelledby="caption-provider-heading" data-testid="caption-provider-card">
		<header class="flex flex-wrap items-center justify-between gap-2">
			<h2 id="caption-provider-heading" class="m-0 text-base font-semibold text-surface-text-2">SyncWords captioning account</h2>
			<span
				v-if="!loading && !loadError"
				class="inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-medium ring-1"
				:class="provider.connected ? 'bg-success-transparent text-success ring-success/30' : 'bg-surface-3 text-surface-8 ring-surface-4'"
				data-testid="provider-state"
			>
				<span class="size-1.5 rounded-full" :class="provider.connected ? 'bg-success' : 'bg-surface-6'" aria-hidden="true" />
				{{ provider.connected ? 'Connected' : 'Not connected' }}
			</span>
		</header>

		<p v-if="loading" class="m-0 text-tiny text-surface-8">Loading…</p>
		<div v-else-if="loadError" class="flex flex-wrap items-center gap-2 text-tiny text-error-light" role="alert">
			<span>{{ loadError }}</span>
			<FormButton type="secondary" size="sm" @click="load">Try again</FormButton>
		</div>

		<template v-else-if="provider.connected">
			<div class="flex flex-col gap-1" data-testid="provider-account">
				<div class="flex flex-wrap items-center gap-2">
					<span class="text-sm font-medium text-surface-text-2" data-testid="provider-account-name">{{ provider.account.name }}</span>
					<span v-if="provider.account.isTrialAccount" class="rounded-full bg-main-transparent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-main-light" data-testid="provider-trial">Trial</span>
				</div>
				<span class="text-xs text-surface-8" data-testid="provider-connected-line">{{ connectedLine }}</span>
				<span v-if="provider.lastError" class="text-xs text-gold" data-testid="provider-last-error">Last check: {{ provider.lastError }}</span>
			</div>
			<p class="m-0 text-xs text-surface-7">New captioning runs on this SyncWords account and its minutes; Castr's captioning hours don't apply to it.</p>
			<div>
				<FormButton type="secondary" size="md" data-testid="provider-disconnect" @click="openDisconnect">Disconnect</FormButton>
			</div>
		</template>

		<form v-else class="flex flex-col gap-3" data-testid="provider-connect" @submit.prevent="connect">
			<p class="m-0 text-tiny text-surface-8">Run captioning on your own SyncWords account and minutes.</p>
			<FormInput id="caption-provider-key" v-model="apiKey" label="Public key" placeholder="SyncWords public key" :maxlength="KEY_MAX" autocomplete="off" :disabled="connecting" />
			<div class="flex flex-col">
				<label for="caption-provider-secret" class="mb-2 block text-tiny font-medium text-surface-text-1">Private key</label>
				<div class="relative">
					<FormInput
						id="caption-provider-secret"
						v-model="apiSecret"
						:type="revealSecret ? 'text' : 'password'"
						placeholder="SyncWords private key"
						custom-classes="!pr-10"
						:maxlength="KEY_MAX"
						autocomplete="new-password"
						:disabled="connecting"
					/>
					<button
						type="button"
						class="absolute right-1 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded text-surface-7 hover:text-white"
						:aria-label="revealSecret ? 'Hide private key' : 'Show private key'"
						@click="revealSecret = !revealSecret"
					>
						<icon-base width="16" height="16" fill="none">
							<icon-eye-crossed v-if="revealSecret" />
							<icon-eye v-else />
						</icon-base>
					</button>
				</div>
			</div>
			<p v-if="connectError" class="m-0 text-xs text-error-light" role="alert" :data-error-code="connectError.code || undefined" data-testid="provider-error">{{ connectError.message }}</p>
			<p v-else class="m-0 text-xs text-surface-7">The keys are checked with SyncWords, stored encrypted, and never shown again.</p>
			<div>
				<FormButton type="main" size="md" :disabled="!canConnect" :is-loading="connecting" @click="connect">Connect</FormButton>
			</div>
		</form>

		<ModalBox v-model="disconnectOpen" title="Disconnect your SyncWords account?" size="sm" mobile-placement="bottom">
			<div class="flex flex-col gap-3 px-4 pb-4 text-sm text-surface-9 sm:px-6 sm:pb-6" data-testid="provider-disconnect-modal">
				<p class="m-0">New captioning will run on Castr's captioning service again. Captioning already created on your SyncWords account stays, but can't start until you reconnect that account.</p>
				<Alert v-if="disconnectError" type="error" role="alert" data-testid="provider-disconnect-error">
					<span class="flex flex-col gap-1">
						<span>{{ disconnectError }}</span>
						<ul v-if="runningFlows.length" class="m-0 list-disc pl-4" data-testid="provider-running-flows">
							<li v-for="f in runningFlows" :key="f.id">
								<router-link :to="{ name: 'CaptioningManage', params: { flowId: f.id } }" class="font-medium text-white underline" @click="disconnectOpen = false">{{ f.streamName || 'Captioning' }}</router-link>
							</li>
						</ul>
					</span>
				</Alert>
			</div>
			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton type="red" size="md" class="w-full justify-center sm:w-auto" :is-loading="disconnecting" data-testid="provider-disconnect-confirm" @click="disconnect">Disconnect</FormButton>
					<FormButton type="secondary" size="md" class="w-full justify-center sm:w-auto" :disabled="disconnecting" @click="disconnectOpen = false">Cancel</FormButton>
				</footer>
			</template>
		</ModalBox>
	</section>
</template>
